/**
 * One dataset item in full, opened from the items table at `?item=<item_id>`.
 * The table clips long values to two lines; this panel shows every field, long
 * text on one line until clicked. The URL holds the item, so a pasted link opens the same one, and
 * previous / next step through the rows of the current page.
 *
 * An item is four slots — input, expected output, output (a recorded answer)
 * and metadata — each a JSON tree (or plain text). A flat item's input keys are
 * the agent's input names; a legacy item (`{variables: {...}, user_prompt, ...}`)
 * shows its variables first, then its other keys. An item pulled from traces
 * links to its trace in Langfuse.
 */

import { inputEntries } from "@/lib/format";
import { ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { DetailSection } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { JsonView } from "@/components/ui/json-view";
import { SidePanel } from "@/components/ui/overlay";
import type { DatasetItem } from "./types";

export function DatasetItemPanel({
  items,
  itemId,
  onOpen,
  onClose,
}: {
  items: DatasetItem[];
  itemId: string;
  onOpen: (id: string) => void;
  onClose: () => void;
}) {
  const index = items.findIndex((it) => it.id === itemId);
  const item = items[index];
  const prev = items[index - 1];
  const next = items[index + 1];
  return (
    <SidePanel
      width={620}
      onClose={onClose}
      title={item ? `Item ${item.position + 1}` : "Item"}
      actions={
        item && (
          <>
            <Button icon aria-label="Previous item" disabled={!prev} onClick={() => prev && onOpen(prev.id)}>
              <ChevronUp size={14} strokeWidth={2.2} />
            </Button>
            <Button icon aria-label="Next item" disabled={!next} onClick={() => next && onOpen(next.id)}>
              <ChevronDown size={14} strokeWidth={2.2} />
            </Button>
          </>
        )
      }
    >
      {!item ? (
        <p className="text-[13px] text-muted">This item is not on the current page.</p>
      ) : (
        <>
          {item.trace_url && (
            <a
              className="link inline-flex items-center gap-1 self-start text-xs"
              href={item.trace_url}
              target="_blank"
              rel="noreferrer"
            >
              Open trace
              <ExternalLink size={12} strokeWidth={2} aria-hidden />
            </a>
          )}
          <DetailSection title="Input">
            <JsonView value={Object.fromEntries(inputEntries(item.input))} label="Input" />
          </DetailSection>
          <DetailSection title="Expected output">
            <JsonView value={item.expected_output} label="Expected output" />
          </DetailSection>
          <DetailSection title="Output">
            <JsonView value={item.output} label="Output" />
          </DetailSection>
          <DetailSection title="Metadata">
            <JsonView value={item.metadata} label="Metadata" />
          </DetailSection>
        </>
      )}
    </SidePanel>
  );
}
